import { useMemo, useCallback } from 'react';
import { Heart, MessageCircle, ChevronRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { useNotifications } from '../lib/queries';
import usePushBus from '../hooks/usePushBus';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Persistent home-page banner that surfaces unread peer interactions
 * (likes, reactions, comments) on the user's posts.
 *
 * Mirrors the visual language of `<SupportReplyBanner />`. Tap fires
 * the `slimmy:open-bell` event so `<NotificationBell />` can open
 * itself; opening the bell triggers `markAllNotificationsRead` which
 * resets the unread count, making this banner self-hide.
 *
 * Why not toast? The product owner explicitly asked to remove the
 * volatile toast for peer interactions and keep only this persistent
 * banner — so a missed notification is never lost in a 3-s timer.
 */
const INTERACTION_TYPES = new Set([
  'reaction',
  'comment',
  'comment_reaction',
  'challenge_post',
  'follow',
]);

export default function InteractionsBanner() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: notifications = [] } = useNotifications();

  // Live refresh on incoming push so the banner appears in the same
  // second a peer reacts/comments instead of waiting for the next
  // 30 s React-Query poll.
  const refresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['notifications', 'list'] });
    qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
  }, [qc]);
  usePushBus(refresh);

  const stats = useMemo(() => {
    let total = 0;
    let likes = 0;
    let comments = 0;
    let lastFromName = null;
    for (const n of notifications) {
      if (n.read) continue;
      const type = (n.type || '').toLowerCase();
      if (!INTERACTION_TYPES.has(type)) continue;
      total += 1;
      if (type === 'reaction' || type === 'comment_reaction') likes += 1;
      else if (type === 'comment') comments += 1;
      if (!lastFromName) lastFromName = n.from_user_name || n.actor_name;
    }
    return { total, likes, comments, lastFromName };
  }, [notifications]);

  if (stats.total === 0) return null;

  // Body copy: prefer the named-actor variant when we have one fresh
  // event ("Mario ha messo like al tuo post"), otherwise fall back to
  // an aggregate count ("3 nuove interazioni sui tuoi post").
  let body;
  if (stats.total === 1 && stats.lastFromName) {
    if (stats.likes === 1) {
      body = t('interactions.singleLike', { name: stats.lastFromName });
    } else if (stats.comments === 1) {
      body = t('interactions.singleComment', { name: stats.lastFromName });
    } else {
      body = t('interactions.singleGeneric', { name: stats.lastFromName });
    }
  } else {
    body = t('interactions.multipleGeneric', { count: stats.total });
  }

  const handleTap = () => {
    // Ask the bell to open itself; that flow will mark all read and
    // invalidate the cache, which collapses this banner.
    window.dispatchEvent(new CustomEvent('slimmy:open-bell'));
  };

  // Pick the dominant icon. Equal counts? Prefer the heart, it's the
  // friendlier symbol — the comment icon shows up only when comments
  // are strictly more frequent than likes.
  const Icon = stats.comments > stats.likes ? MessageCircle : Heart;

  return (
    <div className="px-4 mb-3" style={{ marginTop: '20px' }} data-testid="interactions-banner">
      <button
        type="button"
        onClick={handleTap}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl active:scale-[0.98] transition-transform shadow-sm"
        style={{
          background: isDark
            ? 'linear-gradient(135deg, rgba(232,51,74,0.18) 0%, rgba(168,85,247,0.15) 100%)'
            : 'linear-gradient(135deg, rgba(232,51,74,0.10) 0%, rgba(168,85,247,0.10) 100%)',
          border: `1px solid ${isDark ? 'rgba(232,51,74,0.35)' : 'rgba(232,51,74,0.25)'}`,
        }}
      >
        <span
          className="relative w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #E8334A 0%, #FF6B7B 100%)' }}
        >
          <Icon className="w-4 h-4 text-white" />
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-white text-[#E8334A] text-[10px] font-bold flex items-center justify-center px-1 ring-2 ring-[#E8334A]/40">
            {stats.total > 9 ? '9+' : stats.total}
          </span>
        </span>

        <span className="flex-1 text-left min-w-0">
          <span className={`block text-sm font-semibold leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('interactions.title')}
          </span>
          <span className={`block text-[11px] truncate leading-tight mt-0.5 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
            {body}
          </span>
        </span>

        <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-white/50' : 'text-gray-400'}`} />
      </button>
    </div>
  );
}
